import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useWorkspace } from '../context/WorkspaceContext';
import { getShapeUpAnalytics, getActiveCycleAnalytics, getShapeUpPeople } from '../utils/shapeUpApi';
import { getBugAnalytics } from '../utils/bugsApi';
import BugAnalytics from '../components/shapeup/BugAnalytics';

// Shared Recharts palette, matching the rest of the app's charts.
const AXIS = '#CBD5E1';
const GRID = '#E2E8F0';
const TICK = '#64748B';
const GREEN = '#10B981';
const AMBER = '#F59E0B';
const RED = '#EF4444';
const BLUE = '#3B82F6';
const PURPLE = '#8B5CF6';

// A rate is null when nothing could be measured; show a dash rather than 0%.
const pct = (rate) => (rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`);

// Recharts cannot plot null, but a straight percentage read is easier to reason
// about on the axis. Rates ride in as 0–100 with nulls preserved for gaps.
const toPct = (rate) => (rate === null || rate === undefined ? null : Math.round(rate * 100));

const percentTick = (value) => `${value}%`;

const KpiCard = ({ label, value, hint }) => (
  <Paper elevation={2} sx={{ p: 2.5, flex: '1 1 160px', minWidth: 160 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>{value}</Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">{hint}</Typography>
    )}
  </Paper>
);

const ChartCard = ({ title, description, children }) => (
  <Paper elevation={2} sx={{ p: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>
    )}
    {children}
  </Paper>
);

// Tab identity lives in the URL as ?tab=<slug> so each tab is a shareable link.
const TAB_SLUGS = ['active', 'trends', 'people', 'bugs'];

// Neither People nor Bugs depends on any cycle having closed, so both stay
// reachable on a workspace whose first two tabs have nothing to show.
const TAB_PEOPLE = 2;
const TAB_BUGS = 3;

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}
      id={`shapeup-analytics-tabpanel-${index}`}
      aria-labelledby={`shapeup-analytics-tab-${index}`}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}
function a11yProps(index) {
  return { id: `shapeup-analytics-tab-${index}`, 'aria-controls': `shapeup-analytics-tabpanel-${index}` };
}

// A hill position reads better as a two-decimal fraction than a raw float.
const position2 = (value) => (value === null || value === undefined ? '—' : Number(value).toFixed(2));

// `owner/repo` is mostly org prefix, and the prefix is the same for every repo in
// the list, so only the part that distinguishes them is shown.
const shortRepo = (fullName) => String(fullName || '').split('/').pop();

/**
 * Who has shipped what, and who is carrying how much right now.
 *
 * Everything here is derived from GitHub activity already linked to scopes, so
 * there is nothing to maintain and nothing to get out of date. The load column is
 * the one to read before staffing a cycle: two current bets against one person is
 * the shape that ends with dots that stop moving.
 */
const PeopleTable = ({ people = [] }) => {
  if (!people.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nobody in this organization yet.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Person</TableCell>
          <TableCell align="right">On now</TableCell>
          <TableCell>Areas</TableCell>
          <TableCell>Shipped in</TableCell>
          <TableCell>Recent bets</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {people.map(person => (
          <TableRow key={person.userId} hover>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{person.name}</Typography>
              {person.needsGithubLogin && (
                <Typography variant="caption" color="warning.main">
                  No GitHub login set
                </Typography>
              )}
            </TableCell>
            <TableCell align="right">
              {person.currentBetCount > 1 ? (
                <Tooltip title={`On ${person.currentBets.map(bet => bet.name).join(', ')}`}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${person.currentBetCount} bets`}
                  />
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {person.currentBetCount || '—'}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              {!person.areas?.length ? (
                <Typography variant="caption" color="text.secondary">—</Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {person.areas.map(area => (
                    <Tooltip
                      key={area.slug}
                      title={`${area.betCount} ${area.betCount === 1 ? 'bet' : 'bets'} tagged ${area.label}`}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={area.label}
                        component={RouterLink}
                        to={`/shape-up/tags/${encodeURIComponent(area.slug)}`}
                        clickable
                      />
                    </Tooltip>
                  ))}
                </Box>
              )}
            </TableCell>
            <TableCell>
              {person.repos.length === 0 ? (
                <Typography variant="caption" color="text.secondary">—</Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {person.repos.slice(0, 5).map(repo => (
                    <Tooltip
                      key={repo.name}
                      title={`${repo.name} — ${repo.mergedCount} merged PRs, ${repo.commitCount} commits`}
                    >
                      <Chip size="small" variant="outlined" label={shortRepo(repo.name)} />
                    </Tooltip>
                  ))}
                  {person.repoCount > 5 && (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      +{person.repoCount - 5} more
                    </Typography>
                  )}
                </Box>
              )}
            </TableCell>
            <TableCell>
              <Typography variant="caption" color="text.secondary">
                {person.bets.length
                  ? person.bets.slice(0, 3).map(bet => bet.name).join(', ')
                  : '—'}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Progress-so-far KPIs for the running cycle: how much appetite is spent, how
 * many scopes are in play, and how far the definition of done has been ticked.
 */
const ActiveProgressKpis = ({ progress }) => {
  if (!progress) return null;
  const { betCount, scopeCount, hammeredScopes, cycleDays, daysElapsed, daysRemaining, dod } = progress;
  const dodValue = dod?.configured ? `${dod.checksTicked} / ${dod.checksPossible}` : 'Not configured';
  const dodHint = dod?.configured
    ? `${dod.metCount} of ${scopeCount} scopes fully met`
    : 'No checklist set';

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <KpiCard
        label="Cycle progress"
        value={daysElapsed === null ? '—' : `Day ${daysElapsed}`}
        hint={daysRemaining === null ? `${cycleDays}-day appetite` : `${daysRemaining} of ${cycleDays} days left`}
      />
      <KpiCard label="Live scopes" value={scopeCount} hint={`across ${betCount} ${betCount === 1 ? 'bet' : 'bets'}`} />
      <KpiCard label="Hammered" value={hammeredScopes} hint="scopes cut to fit" />
      <KpiCard label="DoD checks" value={dodValue} hint={dodHint} />
    </Box>
  );
};

/**
 * Live scopes bucketed by slope: uphill (figuring it out), summit (no unknowns),
 * downhill (making it happen), done. Each bar keeps the hill's own colour.
 */
const HillDistributionChart = ({ distribution }) => {
  const data = [
    { name: 'Uphill', count: distribution.uphill, fill: BLUE },
    { name: 'Summit', count: distribution.summit, fill: PURPLE },
    { name: 'Downhill', count: distribution.downhill, fill: AMBER },
    { name: 'Done', count: distribution.done, fill: GREEN },
  ];

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="name" tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
        <YAxis allowDecimals={false} tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
        <RechartsTooltip />
        <Bar dataKey="count" name="Scopes">
          {data.map(entry => <Cell key={entry.name} fill={entry.fill} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Average hill position per day against an even pace line. The dashed target is
 * where a dead-straight climb would sit; the solid line is where the work is.
 */
const BurnupChart = ({ burnup }) => (
  <ResponsiveContainer width="100%" height={300}>
    <LineChart data={burnup} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
      <XAxis dataKey="date" tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
      <YAxis domain={[0, 1]} tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
      <RechartsTooltip formatter={(value) => position2(value)} />
      <Legend />
      <Line type="monotone" dataKey="avgPosition" name="Avg hill position" stroke={BLUE} strokeWidth={2} connectNulls dot={false} />
      <Line type="monotone" dataKey="target" name="Even pace" stroke={TICK} strokeDasharray="4 4" strokeWidth={2} dot={false} />
    </LineChart>
  </ResponsiveContainer>
);

/**
 * Total live scope count per day. A rising line is scope creep: work being added
 * after the bet was placed rather than shaped into it up front.
 */
const ScopeCountChart = ({ scopeCountSeries }) => (
  <ResponsiveContainer width="100%" height={260}>
    <AreaChart data={scopeCountSeries} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
      <XAxis dataKey="date" tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
      <YAxis allowDecimals={false} tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
      <RechartsTooltip />
      <Area type="monotone" dataKey="scopeCount" name="Scopes" stroke={PURPLE} fill={PURPLE} fillOpacity={0.2} />
    </AreaChart>
  </ResponsiveContainer>
);

/**
 * The scopes whose dots have stopped moving, with a one-line read on whether the
 * hills are still being maintained by hand rather than inherited from Jira.
 */
const StuckWatchlist = ({ stuck, judgment }) => {
  const scopes = stuck?.scopes || [];
  const staleLine = judgment
    ? `${judgment.staleCount} of ${judgment.ratedCount} hills unjudged for ${judgment.staleAfterDays}+ days · `
      + `${judgment.unjudgedScopes} scopes never placed by hand`
    : null;

  return (
    <Box>
      {scopes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing has stalled past {stuck?.threshold ?? '—'} days — every live dot has moved recently.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scope</TableCell>
              <TableCell>Bet</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell align="right">Days still</TableCell>
              <TableCell align="right">Position</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scopes.map(scope => (
              <TableRow key={scope.id}>
                <TableCell>
                  {scope.jiraUrl ? (
                    <Link href={scope.jiraUrl} target="_blank" rel="noopener">
                      {scope.jiraKey || scope.jiraSummary}
                    </Link>
                  ) : (scope.jiraKey || scope.jiraSummary)}
                </TableCell>
                <TableCell>{scope.betName}</TableCell>
                <TableCell>{scope.assigneeName || '—'}</TableCell>
                <TableCell align="right">{scope.days}</TableCell>
                <TableCell align="right">{position2(scope.position)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {staleLine && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {staleLine}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Cross-cycle Shape Up analytics, scoped by organization and team. Only complete
 * cycles are trended; anything still running is called out separately so a
 * half-finished cycle never drags a trend line down.
 */
const ShapeUpAnalyticsPage = () => {
  const { organizations, currentOrganization, teams, refreshTeams } = useWorkspace();

  const [organizationId, setOrganizationId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [people, setPeople] = useState(null);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [bugData, setBugData] = useState(null);
  const [bugsLoading, setBugsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the source of truth for the open tab; an unknown/absent slug
  // falls back to the Active cycle tab until the default effect resolves it.
  const tabIndex = TAB_SLUGS.indexOf(searchParams.get('tab'));
  const tabValue = tabIndex === -1 ? 0 : tabIndex;
  const handleTabChange = (event, newValue) => {
    setSearchParams((prev) => {
      prev.set('tab', TAB_SLUGS[newValue]);
      return prev;
    });
  };

  useEffect(() => {
    if (currentOrganization?.id) {
      setOrganizationId(currentOrganization.id);
    } else if (organizations.length > 0) {
      setOrganizationId(organizations[0].id);
    } else {
      setLoading(false);
    }
  }, [currentOrganization, organizations]);

  const loadAnalytics = useCallback(async (orgId, team) => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getShapeUpAnalytics({ organizationId: orgId, teamId: team || undefined });
      setData(result);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load analytics.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (organizationId) loadAnalytics(organizationId, teamId);
  }, [organizationId, teamId, loadAnalytics]);

  // The single most-recently-started running cycle: newest start date wins, with
  // creation time as the tiebreak when two share one.
  const activeCycles = data?.activeCycles || [];
  const activeCycle = [...activeCycles].sort((a, b) => (
    (b.startDate || '').localeCompare(a.startDate || '')
    || (b.createdAt || '').localeCompare(a.createdAt || '')
  ))[0] || null;
  const activeCycleId = activeCycle?.cycleId || null;

  // Snap to the most useful tab once data lands, but only when the URL doesn't
  // already pin one — a shared ?tab= link or a manual choice always wins. The
  // one-shot ref keeps later re-renders/refetches from re-defaulting.
  const initialTabRef = useRef(false);
  useEffect(() => {
    if (loading || initialTabRef.current) return;
    initialTabRef.current = true;
    if (TAB_SLUGS.includes(searchParams.get('tab'))) return;
    setSearchParams((prev) => {
      prev.set('tab', activeCycle ? 'active' : 'trends');
      return prev;
    }, { replace: true });
  }, [loading, activeCycle, searchParams, setSearchParams]);

  // Its heavy detail (snapshot-driven trends) is fetched by id only when there is
  // an active cycle, and cleared whenever the selected cycle changes or goes away.
  useEffect(() => {
    if (!activeCycleId) {
      setActiveDetail(null);
      return undefined;
    }
    let cancelled = false;
    setActiveLoading(true);
    setActiveDetail(null);
    getActiveCycleAnalytics(activeCycleId)
      .then(result => { if (!cancelled) setActiveDetail(result); })
      .catch(() => { if (!cancelled) setActiveDetail(null); })
      .finally(() => { if (!cancelled) setActiveLoading(false); });
    return () => { cancelled = true; };
  }, [activeCycleId]);

  // Same deal as People: one aggregate query, loaded only when the tab is opened.
  useEffect(() => {
    if (!organizationId || tabValue !== TAB_BUGS) return undefined;
    let cancelled = false;
    setBugsLoading(true);
    getBugAnalytics(organizationId)
      .then(result => { if (!cancelled) setBugData(result); })
      .catch(() => { if (!cancelled) setBugData(null); })
      .finally(() => { if (!cancelled) setBugsLoading(false); });
    return () => { cancelled = true; };
  }, [organizationId, tabValue]);

  // People is one aggregate query per organization, so it waits until the tab is
  // opened rather than riding along with every analytics load.
  useEffect(() => {
    if (!organizationId || tabValue !== TAB_PEOPLE) return undefined;
    let cancelled = false;
    setPeopleLoading(true);
    getShapeUpPeople(organizationId)
      .then(result => { if (!cancelled) setPeople(result); })
      .catch(() => { if (!cancelled) setPeople(null); })
      .finally(() => { if (!cancelled) setPeopleLoading(false); });
    return () => { cancelled = true; };
  }, [organizationId, tabValue]);

  const handleOrgChange = async (e) => {
    const orgId = e.target.value;
    setOrganizationId(orgId);
    setTeamId('');
    await refreshTeams(orgId);
  };

  if (!loading && organizations.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <InsightsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h5" gutterBottom>Analytics need an organization</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Shape Up analytics roll up the cycles that belong to an organization.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/organizations">
            Set up an organization
          </Button>
        </Paper>
      </Container>
    );
  }

  const series = data?.series || [];
  const totals = data?.totals || null;
  const dodConfigured = data?.definitionOfDone?.configured;

  // Flatten the nested rollups into the flat rows Recharts reads.
  const outcomeData = series.map(row => ({
    name: row.name,
    shipped: row.outcomes.shipped,
    circuit_breaker: row.outcomes.circuit_breaker,
    abandoned: row.outcomes.abandoned,
    open: row.outcomes.open,
  }));
  const appetiteData = series.map(row => ({
    name: row.name,
    appetiteAccuracy: toPct(row.appetiteAccuracy),
    shippingRate: toPct(row.shippingRate),
  }));
  const shapingData = series.map(row => ({
    name: row.name,
    rebetRate: toPct(row.shaping.rebetRate),
    hammeredRate: toPct(row.shaping.hammeredRate),
  }));
  const dodData = series.map(row => ({
    name: row.name,
    complianceRate: toPct(row.dod.complianceRate),
  }));

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
        </Box>
      );
    }

    // People and Bugs both read the organization rather than its cycles, so they
    // are still worth showing on a workspace that has not closed anything yet. A
    // workspace with no cycles at all can very much still have bugs.
    if (series.length === 0 && activeCycles.length === 0
      && tabValue !== TAB_PEOPLE && tabValue !== TAB_BUGS) {
      return (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No cycles to analyze yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: 'auto' }}>
            Once you close a cycle, its outcomes, appetite accuracy, and shaping quality show up
            here so you can watch the trend across cycles.
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        <TabPanel value={tabValue} index={0}>
          {activeCycle ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Active cycle</Typography>
              <Typography variant="body2" color="text.secondary">
                {activeCycle.name} — how the running cycle is tracking right now, before it closes.
              </Typography>
            </Box>

            {activeLoading || !activeDetail ? (
              <>
                <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              </>
            ) : (
              <>
                <ActiveProgressKpis progress={activeDetail.progress} />

                <ChartCard
                  title="Hill distribution"
                  description="Where the live scopes sit on the hill right now: uphill, at the summit, downhill, or done."
                >
                  <HillDistributionChart distribution={activeDetail.distribution} />
                </ChartCard>

                {activeDetail.burnup.length > 0 ? (
                  <ChartCard
                    title="Hill burn-up over time"
                    description="Average hill position each day against an even pace from start to finish."
                  >
                    <BurnupChart burnup={activeDetail.burnup} />
                  </ChartCard>
                ) : (
                  <ChartCard title="Hill burn-up over time">
                    <Typography variant="body2" color="text.secondary">
                      History starts once dots begin moving.
                    </Typography>
                  </ChartCard>
                )}

                {activeDetail.scopeCountSeries.length > 0 && (
                  <ChartCard
                    title="Scope count over time"
                    description="Total live scopes per day — a rising line is scope creep after the bet was placed."
                  >
                    <ScopeCountChart scopeCountSeries={activeDetail.scopeCountSeries} />
                  </ChartCard>
                )}

                <ChartCard
                  title="Stuck-scope watchlist"
                  description="Live scopes whose dots have stopped moving, and how fresh the hills are."
                >
                  <StuckWatchlist stuck={activeDetail.stuck} judgment={activeDetail.judgment} />
                </ChartCard>
              </>
            )}
          </Box>
          ) : (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No cycle is running right now — check Cross-cycle trends for closed-cycle history.
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {totals && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <KpiCard label="Shipping rate" value={pct(totals.shippingRate)} hint={`${totals.shipped} of ${totals.betCount} bets`} />
            <KpiCard label="Appetite accuracy" value={pct(totals.appetiteAccuracy)} hint={`${totals.appetiteWithin} of ${totals.appetiteMeasurable} measurable`} />
            <KpiCard label="Re-bet rate" value={pct(totals.rebetRate)} hint={`${totals.returningBets} returning bets`} />
            <KpiCard label="Hammered rate" value={pct(totals.hammeredRate)} hint={`${totals.hammeredScopes} of ${totals.totalScopes} scopes`} />
            <KpiCard label="DoD compliance" value={pct(totals.dodComplianceRate)} hint={dodConfigured ? `${totals.dodMet} of ${totals.dodLiveScopes} scopes` : 'Not configured'} />
          </Box>
        )}

        {activeCycles.length > 0 && (
          <Alert severity="info">
            {activeCycles.length} in-progress {activeCycles.length === 1 ? 'cycle is' : 'cycles are'} excluded
            from the trends below.
          </Alert>
        )}

        {series.length === 0 ? (
          <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>No completed cycles yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: 'auto' }}>
              Trends need at least one closed cycle. Close a running cycle to start building the picture.
            </Typography>
          </Paper>
        ) : (
          <>
            <ChartCard
              title="Outcomes per cycle"
              description="How each cycle's bets ended: shipped, hit the circuit breaker, abandoned, or still open."
            >
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={outcomeData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="name" tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                  <YAxis allowDecimals={false} tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="shipped" name="Shipped" stackId="outcomes" fill={GREEN} />
                  <Bar dataKey="circuit_breaker" name="Circuit breaker" stackId="outcomes" fill={AMBER} />
                  <Bar dataKey="abandoned" name="Abandoned" stackId="outcomes" fill={RED} />
                  <Bar dataKey="open" name="Open" stackId="outcomes" fill={BLUE} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Appetite accuracy"
              description="How often bets finished within their appetite, against how often the cycle shipped. The reference line marks 70%."
            >
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={appetiteData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="name" tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                  <YAxis domain={[0, 100]} tickFormatter={percentTick} tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                  <RechartsTooltip formatter={(value) => (value === null ? '—' : `${value}%`)} />
                  <Legend />
                  <ReferenceLine y={70} stroke={TICK} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="appetiteAccuracy" name="Appetite accuracy" stroke={BLUE} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="shippingRate" name="Shipping rate" stroke={GREEN} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Shaping quality"
              description="Re-bet rate is work that came back for another cycle; hammered rate is scopes that were cut to fit the appetite."
            >
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={shapingData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="name" tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                  <YAxis domain={[0, 100]} tickFormatter={percentTick} tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                  <RechartsTooltip formatter={(value) => (value === null ? '—' : `${value}%`)} />
                  <Legend />
                  <Bar dataKey="rebetRate" name="Re-bet rate" fill={AMBER} />
                  <Bar dataKey="hammeredRate" name="Hammered rate" fill={PURPLE} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {dodConfigured ? (
              <ChartCard
                title="Definition of Done"
                description="How many live scopes met the full checklist in each cycle."
              >
                {totals && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Overall compliance</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{pct(totals.dodComplianceRate)}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={totals.dodComplianceRate ? Math.round(totals.dodComplianceRate * 100) : 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={dodData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                    <XAxis dataKey="name" tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                    <YAxis domain={[0, 100]} tickFormatter={percentTick} tick={{ fill: TICK, fontSize: 12 }} stroke={AXIS} />
                    <RechartsTooltip formatter={(value) => (value === null ? '—' : `${value}%`)} />
                    <Bar dataKey="complianceRate" name="DoD compliance" fill={GREEN} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            ) : (
              <ChartCard title="Definition of Done">
                <Typography variant="body2" color="text.secondary">
                  No definition of done is configured for this organization, so there is nothing to
                  measure compliance against. Add a checklist in{' '}
                  <RouterLink to="/settings">Shape Up settings</RouterLink> to track it here.
                </Typography>
              </ChartCard>
            )}
          </>
        )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={TAB_PEOPLE}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>People</Typography>
              <Typography variant="body2" color="text.secondary">
                What each person has shipped, and how much they are carrying right now.
                Read off GitHub activity already linked to scopes, so there is nothing here
                to keep up to date.
              </Typography>
            </Box>

            {peopleLoading ? (
              <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
            ) : !people ? (
              <Alert severity="info">Could not load people for this organization.</Alert>
            ) : (
              <>
                {!people.githubEvidence && (
                  <Alert severity="info">
                    No GitHub activity is linked to any scope yet, so the shipped-in column is
                    empty. Connect the GitHub app in organization settings and the repositories
                    fill in as pull requests land.
                  </Alert>
                )}

                {!people.tagEvidence && (
                  <Alert severity="info">
                    No bet carries a tag yet, so the areas column is empty. Tag a bet with the
                    kind of work it is when you create or edit it, and each person's areas fill
                    in from the bets they were on — nobody has to maintain a list.
                  </Alert>
                )}

                {people.withoutGithubLogin > 0 && (
                  <Alert severity="warning">
                    {people.withoutGithubLogin === 1
                      ? 'One person has no GitHub login set'
                      : `${people.withoutGithubLogin} people have no GitHub login set`}
                    , so their pull requests cannot be attributed to them. It is a single field
                    on <Link component={RouterLink} to="/settings">their settings page</Link>.
                  </Alert>
                )}

                <Paper elevation={2} sx={{ p: 1 }}>
                  <PeopleTable people={people.people} />
                </Paper>
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={TAB_BUGS}>
          {bugsLoading ? (
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
          ) : (
            <BugAnalytics data={bugData} />
          )}
        </TabPanel>
      </>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InsightsIcon fontSize="small" />
          Shape Up Analytics
        </Typography>
      </Breadcrumbs>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        flexWrap: 'wrap', gap: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="Shape Up analytics tabs">
          <Tab label="Active cycle" {...a11yProps(0)} />
          <Tab label="Cross-cycle trends" {...a11yProps(1)} />
          <Tab label="People" {...a11yProps(TAB_PEOPLE)} />
          <Tab label="Bugs" {...a11yProps(TAB_BUGS)} />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', pb: 1 }}>
          {organizations.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Organization</InputLabel>
              <Select value={organizationId} label="Organization" onChange={handleOrgChange}>
                {organizations.map(org => (
                  <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {teams.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Team</InputLabel>
              <Select value={teamId} label="Team" onChange={(e) => setTeamId(e.target.value)}>
                <MenuItem value="">All teams</MenuItem>
                {teams.map(team => (
                  <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {renderBody()}
    </Container>
  );
};

export default ShapeUpAnalyticsPage;
